import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
    AppNotification,
    getNotificationState,
    markNotificationsRead,
    refreshNotifications,
    subscribeNotifications,
    unreadNotifications,
} from '../services/notificationStore';

/** Abrufabstand, solange die Seite sichtbar ist. */
export const NOTIFICATION_POLL_MS = 60_000;

export interface UseNotificationsResult {
    items: AppNotification[];
    unread: AppNotification[];
    unreadCount: number;
    /** Nur der ERSTE Abruf; danach bleibt der alte Stand sichtbar. */
    loading: boolean;
    error: boolean;
    refresh: () => Promise<void>;
    markRead: (n: AppNotification) => Promise<void>;
    markAllRead: () => Promise<void>;
}

/**
 * Mitteilungen des angemeldeten Nutzers, gemeinsam fuer alle Ansichten.
 *
 * Jede Instanz stoesst beim Einhaengen, bei Sichtbarkeit/Fokus und im
 * Minutentakt einen Abruf an; der Speicher legt parallele Abrufe zusammen und
 * drosselt auf hoechstens einen je MIN_REFRESH_GAP_MS. Mehrere Instanzen
 * (Navigation + Dashboard) kosten also keine doppelten Anfragen.
 *
 * `enabled = false` haelt den Hook still (z. B. Freelancer-Portal).
 */
export const useNotifications = (userId: string | null | undefined, enabled = true): UseNotificationsResult => {
    const state = useSyncExternalStore(subscribeNotifications, getNotificationState, getNotificationState);
    const active = enabled && !!userId;

    useEffect(() => {
        if (!active || !userId) return;
        refreshNotifications(userId);
        const tick = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            refreshNotifications(userId);
        };
        const onVisible = () => { if (document.visibilityState === 'visible') refreshNotifications(userId); };
        const timer = window.setInterval(tick, NOTIFICATION_POLL_MS);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', tick);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', tick);
        };
    }, [active, userId]);

    const own = active && state.userId === userId;
    const items = own ? state.items : [];
    const unread = unreadNotifications(items);

    const refresh = useCallback(async () => {
        if (userId) await refreshNotifications(userId, { force: true });
    }, [userId]);

    // Nur der Status der Mitteilung. Frueher wurde zusaetzlich `readBy` an der
    // Ursprungsnotiz nachgetragen — mit einem VOLLEN Projektstand aus der
    // geladenen Liste (Codex-Review 25.09.2026: ueberschreibt neuere Daten
    // anderer Bearbeiter). `readBy` liest keine Ansicht aus; der Schreibweg
    // war reines Risiko und ist deshalb entfallen.
    const markRead = useCallback(async (n: AppNotification) => {
        if (!userId) return;
        await markNotificationsRead([n.id]);
    }, [userId]);

    const markAllRead = useCallback(async () => {
        if (!userId) return;
        const offen = unreadNotifications(getNotificationState().items);
        if (offen.length === 0) return;
        await markNotificationsRead(offen.map(n => n.id));
    }, [userId]);

    return {
        items,
        unread,
        unreadCount: unread.length,
        loading: own ? state.loading : active,
        error: own ? state.error : false,
        refresh,
        markRead,
        markAllRead,
    };
};
